import { useEffect, useMemo, useState } from 'react';
import Api from '../../../../../constants/Api';
import { useBranch } from '../../../../../context/BranchContext';
import LoadingCenter from '../../../Common/LoadingCenter';
import axiosInstance from '../../../../../services/AxiosInstance';
import { Row, Col } from 'react-bootstrap';
import { formatDateTime } from '../../../../../utils/DateUtils';
import { formatPhoneNumber, buildWhatsappUrl } from '../../../../../utils/FormatUtils';

const RetentionCard = ({ label, value, detail, icon, tone }) => (
    <div className={`customer-retention-card customer-retention-card--${tone}`}>
        <div className="customer-retention-card__header">
            <span>{label}</span>
            <div className="customer-retention-card__icon">
                <i className={`fa-solid ${icon}`}></i>
            </div>
        </div>
        <strong>{value}</strong>
        <p>{detail}</p>
    </div>
);

/**
 * Dias de calendário desde a última compra.
 *
 * A conta despreza a hora dos dois lados de propósito. Comparando instantes, uma compra
 * feita ontem às 23h dava "0 dias" às 9h de hoje, e o mesmo desvio empurrava clientes de
 * uma faixa de risco para a outra conforme o horário do atendimento.
 */
const getDaysAgo = (lastOrderDate) => {
    if (!lastOrderDate) return null;

    const last = new Date(lastOrderDate);
    const startOfLastDay = new Date(last.getFullYear(), last.getMonth(), last.getDate());
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return Math.round((startOfToday - startOfLastDay) / (1000 * 60 * 60 * 24));
};

const getInitials = (name) => {
    if (!name) return '?';

    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return parts[0][0].toUpperCase();
};

const getAvatarColor = (index) => {
    const colors = [
        'linear-gradient(135deg, #587388 0%, #7da6c2 100%)',
        'linear-gradient(135deg, #3e5c72 0%, #587388 100%)',
        'linear-gradient(135deg, #b87839 0%, #d9903d 100%)',
        'linear-gradient(135deg, #6f8ca3 0%, #93afc3 100%)',
        'linear-gradient(135deg, #c16e57 0%, #e1967f 100%)',
        'linear-gradient(135deg, #486377 0%, #69879d 100%)'
    ];

    return colors[index % colors.length];
};

const getUrgencyMeta = (daysAgo) => {
    if (daysAgo >= 180) return { label: 'Crítico', className: 'is-critical' };
    if (daysAgo >= 90) return { label: 'Alto risco', className: 'is-high' };
    if (daysAgo >= 60) return { label: 'Atenção', className: 'is-medium' };
    return { label: 'Observação', className: 'is-low' };
};

// Mesmos cortes usados nos contadores e na barra de distribuição.
const getRiskKey = (daysAgo) => {
    if (daysAgo == null) return null;
    if (daysAgo >= 180) return 'critico';
    if (daysAgo >= 90) return 'alto';
    if (daysAgo >= 60) return 'medio';
    return 'baixo';
};

// Quantos clientes a lista mostra antes de pedir para carregar mais. Base grande chegava
// a renderizar a carteira inteira de uma vez, e ninguém liga para mil pessoas hoje.
const PAGE_SIZE = 50;

const CustomerNonReturning = ({ days, onStatsChange }) => {
    const { selectedBranch } = useBranch();
    const [rows, setRows] = useState([]);
    const [showLoading, setShowLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [riskFilter, setRiskFilter] = useState(null);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    useEffect(() => {
        const branchId = selectedBranch ? selectedBranch.id : -1;

        setShowLoading(true);
        axiosInstance.get(`${Api.main.customer}/non-returning/${branchId}/${days}`)
            .then((response) => {
                setRows(response.data || []);
            })
            .finally(() => {
                setShowLoading(false);
            });
    }, [selectedBranch, days]);

    const stats = useMemo(() => {
        if (!rows.length) {
            return { total: 0, critico: 0, alto: 0, medio: 0, baixo: 0, avgDays: 0, medianDays: 0 };
        }

        const allDays = rows
            .map((row) => getDaysAgo(row.lastOrderDate))
            .filter((value) => value != null);
        const total = rows.length;
        const critico = allDays.filter((value) => value >= 180).length;
        const alto = allDays.filter((value) => value >= 90 && value < 180).length;
        const medio = allDays.filter((value) => value >= 60 && value < 90).length;
        const baixo = allDays.filter((value) => value < 60).length;
        const avgDays = allDays.length
            ? Math.round(allDays.reduce((sum, value) => sum + value, 0) / allDays.length)
            : 0;

        // A mediana é o número exibido porque a média mente nesta carteira: quem comprou
        // uma vez há anos e sumiu entra com milhares de dias e puxa a média sozinho. A
        // mediana descreve o cliente típico, que é sobre quem a campanha vai falar.
        const sortedDays = [...allDays].sort((left, right) => left - right);
        const middle = Math.floor(sortedDays.length / 2);
        const medianDays = sortedDays.length === 0
            ? 0
            : sortedDays.length % 2 === 0
                ? Math.round((sortedDays[middle - 1] + sortedDays[middle]) / 2)
                : sortedDays[middle];

        return { total, critico, alto, medio, baixo, avgDays, medianDays };
    }, [rows]);

    useEffect(() => {
        if (onStatsChange) {
            onStatsChange(stats);
        }
    }, [stats, onStatsChange]);

    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();

        return rows.filter((row) => {
            if (riskFilter && getRiskKey(getDaysAgo(row.lastOrderDate)) !== riskFilter) {
                return false;
            }

            if (!query) {
                return true;
            }

            return (row.fullName && row.fullName.toLowerCase().includes(query))
                || (row.phone && row.phone.includes(query));
        });
    }, [rows, search, riskFilter]);

    // Trocar filtro ou busca recomeça a paginação: senão a lista abriria já expandida
    // de um recorte anterior.
    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [search, riskFilter, days, selectedBranch]);

    const visibleRows = filteredRows.slice(0, visibleCount);

    const riskLevels = [
        { key: 'critico', color: '#d95c5c', label: 'Crítico', count: stats.critico },
        { key: 'alto', color: '#d9903d', label: 'Alto risco', count: stats.alto },
        { key: 'medio', color: '#d5b24b', label: 'Atenção', count: stats.medio },
        { key: 'baixo', color: '#4a8d72', label: 'Observação', count: stats.baixo }
    ];

    const priorityRows = useMemo(() => {
        return [...rows]
            .filter((row) => getDaysAgo(row.lastOrderDate) != null)
            .sort((a, b) => (getDaysAgo(b.lastOrderDate) || 0) - (getDaysAgo(a.lastOrderDate) || 0))
            .slice(0, 5);
    }, [rows]);

    if (showLoading) {
        return <LoadingCenter />;
    }

    if (!rows.length) {
        return (
            <div className="customer-empty-panel">
                <i className="fa-solid fa-user-check"></i>
                <strong>Sem clientes em risco neste recorte</strong>
                <p>Não há clientes sem retorno acima de {days} dias para a filial selecionada.</p>
            </div>
        );
    }

    return (
        <div className="customer-retention-dashboard">
            <Row className="g-3 mb-4">
                <Col lg={3} md={6}>
                    <RetentionCard
                        label="Carteira em risco"
                        value={stats.total}
                        detail={`clientes sem retorno há mais de ${days} dias`}
                        icon="fa-users-line"
                        tone="sky"
                    />
                </Col>
                <Col lg={3} md={6}>
                    <RetentionCard
                        label="Críticos"
                        value={stats.critico}
                        detail="180 dias ou mais sem retorno"
                        icon="fa-triangle-exclamation"
                        tone="coral"
                    />
                </Col>
                <Col lg={3} md={6}>
                    <RetentionCard
                        label="Alta prioridade"
                        value={stats.alto}
                        detail="entre 90 e 179 dias"
                        icon="fa-fire"
                        tone="amber"
                    />
                </Col>
                <Col lg={3} md={6}>
                    {/* Sempre dos clientes em risco, não da base: como a janela já corta quem
                        voltou, o valor nunca pode ser menor que a janela escolhida. */}
                    <RetentionCard
                        label="Tempo típico"
                        value={`${stats.medianDays}d`}
                        detail={`mediana dos ${stats.total} sem retorno · média ${stats.avgDays}d`}
                        icon="fa-calendar-xmark"
                        tone="slate"
                    />
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                <Col xl={8}>
                    <div className="customer-panel">
                        <div className="customer-panel__header">
                            <div>
                                <span className="customer-panel__eyebrow">Distribuição de risco</span>
                                <h3 className="customer-panel__title">Segmentação da carteira</h3>
                                <p className="customer-panel__subtitle">Quanto mais a carteira migra para faixas altas, maior a urgência de campanhas de reativação.</p>
                            </div>
                        </div>

                        <div className="customer-panel__body">
                            <div className="customer-risk-bar">
                                {riskLevels.map((item) => item.count > 0 && (
                                    <div
                                        key={item.label}
                                        style={{
                                            width: `${(item.count / stats.total) * 100}%`,
                                            background: item.color
                                        }}
                                        title={`${item.label}: ${item.count}`}
                                    />
                                ))}
                            </div>

                            {/* A legenda também filtra: é onde o operador decide por onde começar. */}
                            <div className="customer-risk-legend">
                                {riskLevels.map((item) => (
                                    <button
                                        type="button"
                                        key={item.label}
                                        className={`customer-risk-legend__item${riskFilter === item.key ? ' is-active' : ''}`}
                                        onClick={() => setRiskFilter(riskFilter === item.key ? null : item.key)}
                                        disabled={item.count === 0}
                                    >
                                        <span className="customer-risk-legend__dot" style={{ background: item.color }} />
                                        <span>{item.label}</span>
                                        <strong>{item.count}</strong>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </Col>

                <Col xl={4}>
                    <div className="customer-panel">
                        <div className="customer-panel__header">
                            <div>
                                <span className="customer-panel__eyebrow">Prioridade de contato</span>
                                <h3 className="customer-panel__title">Clientes mais sensíveis</h3>
                                <p className="customer-panel__subtitle">Os casos abaixo devem ser abordados antes do restante da base.</p>
                            </div>
                        </div>

                        <div className="customer-panel__body customer-watchlist">
                            {priorityRows.map((row, index) => {
                                const daysAgo = getDaysAgo(row.lastOrderDate);
                                const urgency = getUrgencyMeta(daysAgo);

                                return (
                                    <div key={row.id || row.phone || index} className="customer-watchlist__item">
                                        <div
                                            className="customer-watchlist__avatar"
                                            style={{ background: getAvatarColor(index) }}
                                        >
                                            {getInitials(row.fullName)}
                                        </div>

                                        <div className="customer-watchlist__content">
                                            <strong>{row.fullName}</strong>
                                            <span>{daysAgo != null ? `${daysAgo} dias sem retorno` : 'Sem histórico de compra'}</span>
                                        </div>

                                        <span className={`customer-urgency-badge ${urgency.className}`}>
                                            {urgency.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Col>
            </Row>

            <div className="customer-panel">
                <div className="customer-panel__header customer-panel__header--table">
                    <div>
                        <span className="customer-panel__eyebrow">Base priorizada</span>
                        <h3 className="customer-panel__title">Clientes para reativação</h3>
                        <p className="customer-panel__subtitle">
                            {filteredRows.length === rows.length
                                ? `${rows.length} clientes identificados`
                                : `${filteredRows.length} de ${rows.length} clientes`}
                            {riskFilter && <button type="button" className="customer-filter-clear" onClick={() => setRiskFilter(null)}>limpar filtro de risco</button>}
                        </p>
                    </div>

                    <div className="customer-table-search">
                        <i className="fa-solid fa-magnifying-glass"></i>
                        <input
                            type="text"
                            placeholder="Buscar por nome ou telefone"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </div>
                </div>

                <div className="customer-retention-list">
                    {filteredRows.length === 0 ? (
                        <div className="customer-retention-list__empty">
                            <i className="fa-solid fa-magnifying-glass"></i>
                            <p>Nenhum cliente encontrado para o filtro informado.</p>
                        </div>
                    ) : (
                        visibleRows.map((row, index) => {
                            const daysAgo = getDaysAgo(row.lastOrderDate);
                            const urgency = daysAgo != null ? getUrgencyMeta(daysAgo) : null;

                            return (
                                <div key={row.id || row.phone || index} className="customer-retention-row">
                                    <div
                                        className="customer-retention-row__avatar"
                                        style={{ background: getAvatarColor(index) }}
                                    >
                                        {getInitials(row.fullName)}
                                    </div>

                                    <div className="customer-retention-row__identity">
                                        <strong>{row.fullName}</strong>
                                        <span>{row.email || 'Sem e-mail cadastrado'}</span>
                                    </div>

                                    <div className="customer-retention-row__meta">
                                        <span>
                                            <i className="fa-solid fa-phone"></i>
                                            {row.phone ? formatPhoneNumber(row.phone) : 'Sem telefone'}
                                        </span>
                                        <span>
                                            <i className="fa-solid fa-clock-rotate-left"></i>
                                            {daysAgo != null ? `${daysAgo} dias sem retorno` : 'Sem histórico'}
                                        </span>
                                        <span>
                                            <i className="fa-solid fa-calendar-days"></i>
                                            {row.lastOrderDate ? formatDateTime(row.lastOrderDate) : 'Sem histórico'}
                                        </span>
                                    </div>

                                    <div className="customer-retention-row__actions">
                                        {urgency && (
                                            <span className={`customer-urgency-badge ${urgency.className}`}>
                                                {urgency.label}
                                            </span>
                                        )}

                                        {row.phone ? (
                                            <a
                                                href={buildWhatsappUrl(row.phone)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="customer-retention-row__whatsapp"
                                                title="Enviar mensagem no WhatsApp"
                                            >
                                                <i className="fa-brands fa-whatsapp"></i>
                                            </a>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {visibleRows.length < filteredRows.length && (
                    <div className="customer-retention-list__more">
                        <span>Mostrando {visibleRows.length} de {filteredRows.length}</span>
                        <button type="button" onClick={() => setVisibleCount((current) => current + PAGE_SIZE)}>
                            Mostrar mais {Math.min(PAGE_SIZE, filteredRows.length - visibleRows.length)}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerNonReturning;
